/** @jsx jsx */
import { jsx, Flex } from 'theme-ui'
import { func, bool, array } from 'prop-types'
import { useContext, useEffect } from 'react'
import { CfnButton } from './Buttons'
import { useFormikContext } from 'formik'
import { QuestionStepContext } from '../../context/QuestionStepContext'
import { isAnswerInvalid } from '../common/form/InputValidationHelper'
import isEmpty from 'lodash.isempty'
import { cleanUpIsHide } from './form/FormikHelper'

const QuestionButtonRow = ({
  handleContinue,
  handleBack,
  isFinalStep,
  isStrategyBuilderQuestion,
  questionsInView,
  hideBackButton,
  nextButtonDisabled,
  setNextButtonDisabled,
  questionHasTooManyAnswers,
  top = false,
  ...props
}) => {
  const { values, submitForm, validateForm, setTouched } = useFormikContext()
  const {
    setValuesFromFormik,
    setUserHitBack,
    template,
    setNextButtonClicked,
    isAllAnswerValid,
    setIsAllAnswerValid,
  } = useContext(QuestionStepContext)
  const { questionIsPercentGroupSection, firstPercentGroupComplete } = template

  useEffect(() => {
    const isValid = isStrategyBuilderQuestion
      ? questionsInView &&
        questionsInView?.every(group =>
          group?.questions?.every(question => {
            //return true when question is valid, in order to keep .every loop continue
            return !isAnswerInvalid(
              question,
              values,
              questionIsPercentGroupSection,
              firstPercentGroupComplete
            )
          })
        )
      : questionsInView &&
        questionsInView?.every(question => {
          return !isAnswerInvalid(
            question,
            values,
            questionIsPercentGroupSection,
            firstPercentGroupComplete
          )
        })

    setIsAllAnswerValid(isStrategyBuilderQuestion ? isValid : true)
    !questionHasTooManyAnswers && setNextButtonDisabled(false)
    //has invalid answer, disable next button

    if (isStrategyBuilderQuestion) {
      setValuesFromFormik(values)
    }
  }, [values, questionsInView, questionHasTooManyAnswers])

  const validateAndTriggerFormErrors = async () => {
    const errors = await validateForm()
    const obj = Object.keys(errors).map(item => {
      return { [item]: true }
    })
    setTouched(Object.assign({}, ...obj))
    return !isEmpty(errors)
  }

  const handleNextClick = async () => {
    setUserHitBack(false)
    setNextButtonClicked(false)
    if (isFinalStep || isStrategyBuilderQuestion) {
      const hasErrors = await validateAndTriggerFormErrors()
      if (hasErrors) return
      await submitForm()
    }
    handleContinue && handleContinue()
  }

  const handleBackClick = async () => {
    cleanUpIsHide(values)
    setUserHitBack(true)
    if (isStrategyBuilderQuestion) {
      await submitForm()
    }
    handleBack()
  }

  return (
    <Flex
      sx={{
        flexDirection: ['column', 'row'],
        justifyContent: isStrategyBuilderQuestion ? ['flex-start'] : ['center'],
        alignItems: ['center'],
      }}
      {...props}
    >
      {!hideBackButton && (
        <CfnButton
          data-automation-id={`${top ? 'top' : 'bottom'}-back-button`}
          data-testid={`${top ? 'top' : 'bottom'}-back-button`}
          onClick={handleBackClick}
          sx={{
            mr: [0, 5],
            mb: [5, 0],
            px: isStrategyBuilderQuestion ? 7 : 5,
          }}
          color="forest"
          buttonStyle="secondary"
          size="small"
          type="button"
        >
          Back
        </CfnButton>
      )}
      <CfnButton
        data-automation-id={`${top ? 'top' : 'bottom'}-next-button`}
        data-testid={`${top ? 'top' : 'bottom'}-next-button`}
        disabled={nextButtonDisabled}
        type="button"
        onClick={async () => {
          await validateAndTriggerFormErrors()
          setNextButtonDisabled(!isAllAnswerValid)
          setNextButtonClicked(true)
          if (isAllAnswerValid) {
            handleNextClick()
          }
        }}
        buttonStyle="primary"
        color="forest"
        size="small"
        sx={{
          border: '2px',
          borderColor: 'forest',
          borderStyle: 'solid',
          px: isStrategyBuilderQuestion ? 7 : 5,
        }}
      >
        Next
      </CfnButton>
    </Flex>
  )
}

QuestionButtonRow.propTypes = {
  handleContinue: func,
  handleBack: func,
  isFinalStep: bool,
  isStrategyBuilderQuestion: bool,
  questionsInView: array,
  hideBackButton: bool,
  top: bool,
  nextButtonDisabled: bool,
  setNextButtonDisabled: func,
  questionHasTooManyAnswers: bool,
}

export default QuestionButtonRow
